import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useLazyQuery, useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import { GET_INTEGRATIONS_BY_TYPE } from '~/services/store';
import { ADD_GOOGLE_CALENDAR_INTEGRATION, GET_GOOGLE_CALENDAR_INTEGRATION } from '~/services/integration';

 

interface GoogleCalendarIntegration {
  id: string;
  name: string;
  email: string;
  status: 'connected' | 'disconnected';
  calendar_id?: string;
  created_at: string;
}

interface BackendGoogleCalendarIntegration {
  id: string;
  type: string;
  agentId: string;
  configuration: {
    notification_emails?: string[];
    googleCalendarIntegration?: Array<{
      name: string;
      email: string;
      calendarId: string;
    }>;
  };
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

interface GoogleCalendarEvent {
  id: string;
  summary: string;
  description?: string;
  start: {
    dateTime: string;
    timeZone: string;
  };
  end: {
    dateTime: string;
    timeZone: string;
  };
}

const ScheduleApps = () => {
  const [t] = useTranslation('schedule');
  const { agentId } = useParams();
  const [integrations, setIntegrations] = useState<GoogleCalendarIntegration[]>([]);
  const [events, setEvents] = useState<GoogleCalendarEvent[]>([]);
  const [selectedCalendarId, setSelectedCalendarId] = useState<string>('');
  const [toast, setToast] = useState<{message: string, type: 'success' | 'error'} | null>(null);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [newIntegrationName, setNewIntegrationName] = useState<string>('');
  const [newIntegrationEmail, setNewIntegrationEmail] = useState<string>('');
  const [isCreatingIntegration, setIsCreatingIntegration] = useState<boolean>(false);

  const [getIntegrations, { loading }] = useLazyQuery(GET_GOOGLE_CALENDAR_INTEGRATION, {
    variables: { data: { agentId: agentId } },
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const integrationResponse = data?.get_google_calendar_integration;
      if (integrationResponse && !integrationResponse.hasError) {
        const integration: BackendGoogleCalendarIntegration = integrationResponse.data?.integration;
        
        if (integration && integration.configuration?.googleCalendarIntegration) {
          const googleCalendarIntegrations = integration.configuration.googleCalendarIntegration;
          
          // Convert the integration data to our format
          const integrationsArray = googleCalendarIntegrations.map((calendarIntegration: any, index: number) => ({
            id: `${integration.id}-${index}`,
            name: calendarIntegration.name || `Calendar ${index + 1}`,
            email: calendarIntegration.email || '',
            status: integration.active ? 'connected' as const : 'disconnected' as const,
            calendar_id: calendarIntegration.calendarId || '',
            created_at: integration.createdAt || new Date().toISOString()
          }));
          
          setIntegrations(integrationsArray);
          // Select first calendar by default
          if (integrationsArray.length > 0 && integrationsArray[0].calendar_id) {
            setSelectedCalendarId(integrationsArray[0].calendar_id);
          }
        } else {
          setIntegrations([]);
        }
      } else {
        setIntegrations([]);
        if (integrationResponse?.hasError) {
          console.error('Error fetching Google Calendar integration:', integrationResponse.message);
        }
      }
    },
    onError: (error) => {
      console.error('Error fetching Google Calendar integration:', error);
      setIntegrations([]);
      setToast({ 
        message: error.message || t('integration_fetch_error'), 
        type: 'error' 
      });
    },
  });

  const [addGoogleCalendarIntegration] = useMutation(ADD_GOOGLE_CALENDAR_INTEGRATION, {
    onCompleted: (data) => {
      if (data?.add_google_calendar_integration?.hasError) {
        setToast({ 
          message: data.add_google_calendar_integration.message || t('integration_creation_error'), 
          type: 'error' 
        });
      } else {
        setToast({ 
          message: data?.add_google_calendar_integration?.message || t('integration_created_successfully'), 
          type: 'success' 
        });
        resetAddModal();
        // Refresh integrations list
        getIntegrations();
      }
      setIsCreatingIntegration(false);
    },
    onError: (error) => {
      console.error('Error creating Google Calendar integration:', error);
      setToast({ 
        message: error.message || t('integration_creation_error'), 
        type: 'error' 
      });
      setIsCreatingIntegration(false);
    },
  });

  useEffect(() => {
    if (agentId) {
      getIntegrations();
    }
  }, [agentId, getIntegrations]);

  const handleCalendarChange = (calendarId: string) => {
    setSelectedCalendarId(calendarId);
  };

  const connectToGoogleCalendar = () => {
    // Simular conexión a Google Calendar
    const authUrl = `https://accounts.google.com/oauth/authorize?client_id=YOUR_CLIENT_ID&redirect_uri=${window.location.origin}/auth/callback&scope=https://www.googleapis.com/auth/calendar&response_type=code&access_type=offline`;
    window.open(authUrl, '_blank', 'width=500,height=600');
  };

  const handleAddIntegration = async () => {
    if (!newIntegrationName.trim() || !newIntegrationEmail.trim()) {
      setToast({ message: t('please_fill_all_fields'), type: 'error' });
      return;
    }

    if (!agentId) {
      setToast({ message: t('agent_id_required'), type: 'error' });
      return;
    }

    setIsCreatingIntegration(true);

    try {
      await addGoogleCalendarIntegration({
        variables: {
          data: {
            name: newIntegrationName.trim(),
            email: newIntegrationEmail.trim(),
            calendarId: newIntegrationEmail.trim(), // Use email as calendar ID
            agentId: agentId
          }
        }
      });
    } catch (error) {
      console.error('Error in handleAddIntegration:', error);
      setIsCreatingIntegration(false);
    }
  };

  const handleDeleteIntegration = (integrationId: string) => {
    if (window.confirm(t('confirm_delete_integration'))) {
      // TODO: Implement new logic for deleting integration
      console.log('Deleting integration:', integrationId);
    }
  };

  const resetAddModal = () => {
    setShowAddModal(false);
    setNewIntegrationName('');
    setNewIntegrationEmail('');
    setIsCreatingIntegration(false);
  };

  const formatEventTime = (dateTimeString: string) => {
    return new Date(dateTimeString).toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className="toast toast-end toast-top z-50">
          <div className={`alert ${toast.type === 'success' ? 'alert-success' : 'alert-error'} shadow-lg`}>
            <span>{toast.message}</span>
            <button onClick={() => setToast(null)} className="btn btn-sm btn-circle">×</button>
          </div>
        </div>
      )}

      Header
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">{t('integrations')}</h2>
          <p className="text-gray-600 text-sm mt-1">
            {t('apps_description')}
          </p>
          <p className="text-gray-500 text-xs mt-1">
            *{t('apps_disclaimer')}
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn btn-primary"
        >
          {t('add_integration')}
        </button>
      </div>

      {/* Integrations List */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">{t('connected_calendars')}</h3>
        {loading ? (
          <div className="flex justify-center py-8">
            <span className="loading loading-spinner loading-lg"></span>
          </div>
        ) : integrations.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t('no_integrations')}
            </h3>
            <p className="text-gray-500 mb-6">
              {t('no_integrations_description')}
            </p>
            <button
              onClick={() => setShowAddModal(true)}
              className="btn btn-primary"
            >
              {t('add_first_integration')}
            </button>
          </div>
        ) : (
          integrations.map((integration) => (
            <div key={integration.id} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">{integration.name}</h3>
                    <p className="text-sm text-gray-500">{integration.email}</p>
                    <div className="flex items-center mt-1">
                      <div className={`w-2 h-2 rounded-full mr-2 ${
                        integration.status === 'connected' ? 'bg-green-500' : 'bg-red-500'
                      }`}></div>
                      <span className={`text-xs ${
                        integration.status === 'connected' ? 'text-green-700' : 'text-red-700'
                      }`}>
                        {integration.status === 'connected' ? t('connected') : t('disconnected')}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => getIntegrations()}
                    className="btn btn-ghost btn-xs"
                    title={t('refresh') || 'Actualizar'}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Integration Modal */}
      {showAddModal && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-4">{t('add_new_integration')}</h3>
            
            <div className="space-y-4">
              <div>
                <label className="label">
                  <span className="label-text">{t('integration_name')}</span>
                </label>
                <input
                  type="text"
                  placeholder={t('integration_name_placeholder')}
                  className="input input-bordered w-full"
                  value={newIntegrationName}
                  onChange={(e) => setNewIntegrationName(e.target.value)}
                />
              </div>
              
              <div>
                <label className="label">
                  <span className="label-text">{t('google_email')}</span>
                </label>
                <input
                  type="email"
                  placeholder={t('google_email_placeholder')}
                  className="input input-bordered w-full"
                  value={newIntegrationEmail}
                  onChange={(e) => setNewIntegrationEmail(e.target.value)}
                />
                <p className="text-sm text-gray-600 mt-1">
                  *{t('gmail_corporate_note')}
                </p>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>{t('note')}:</strong> {t('integration_note')}
                </p>
                <div className="bg-blue-100 p-3 rounded border-l-4 border-blue-400 mt-3">
                  <p className="text-xs text-blue-900 font-medium">
                    Email del servicio para agregar como invitado:
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="text-xs bg-blue-200 px-2 py-1 rounded text-blue-900 flex-1">
                      google-calendar-api@tbit-351122.iam.gserviceaccount.com
                    </code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText('google-calendar-api@tbit-351122.iam.gserviceaccount.com');
                        setToast({ message: 'Email copiado al portapapeles', type: 'success' });
                      }}
                      className="btn btn-xs btn-primary"
                      title="Copiar email"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-action">
              <button
                onClick={resetAddModal}
                className="btn"
                disabled={isCreatingIntegration}
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleAddIntegration}
                className="btn btn-primary"
                disabled={!newIntegrationName.trim() || !newIntegrationEmail.trim() || isCreatingIntegration}
              >
                {isCreatingIntegration && <span className="loading loading-spinner loading-sm mr-2"></span>}
                {isCreatingIntegration ? t('creating_integration') : t('create_integration')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleApps;
